import { Controller } from "@hotwired/stimulus"

/**
 * MechanicSelectorController
 *
 * Dropdown popover for admin/CRM to select which mechanic's route
 * a ticket should be assigned to when toggling "en ruta".
 *
 * Usage:
 *   <div data-controller="mechanic-selector"
 *        data-mechanic-selector-ticket-id-value="123"
 *        data-mechanic-selector-toggle-url-value="/apps/home-repairs/tickets/123/toggle_en_ruta">
 *     <button data-action="click->mechanic-selector#open">Assign</button>
 *   </div>
 */
export default class extends Controller {
  static targets = ["popover"]
  static values = {
    ticketId: Number,
    toggleUrl: String,
    currentMechanicId: { type: Number, default: 0 }
  }

  connect() {
    this.mechanics = null // cached mechanic list
    this.isOpen = false

    // Close on outside click
    this.handleOutsideClick = (e) => {
      if (this.isOpen && !this.element.contains(e.target)) {
        this.close()
      }
    }
    document.addEventListener('click', this.handleOutsideClick)
  }

  disconnect() {
    document.removeEventListener('click', this.handleOutsideClick)
    this.close()
  }

  async open(event) {
    event.stopPropagation()

    if (this.isOpen) {
      this.close()
      return
    }

    // Load mechanics if not cached
    if (!this.mechanics) {
      await this.loadMechanics()
    }

    this.renderPopover()
    this.isOpen = true
  }

  close() {
    if (this.hasPopoverTarget) {
      this.popoverTarget.classList.add('hidden')
    }
    this.isOpen = false
  }

  async loadMechanics() {
    try {
      const response = await fetch('/apps/home-repairs/tickets/available_mechanics', {
        credentials: 'same-origin',
        headers: { 'Accept': 'application/json' }
      })
      if (response.ok) {
        this.mechanics = await response.json()
      } else {
        this.mechanics = []
      }
    } catch (error) {
      console.error('Error loading mechanics:', error)
      this.mechanics = []
    }
  }

  renderPopover() {
    if (!this.hasPopoverTarget) return

    if (!this.mechanics || this.mechanics.length === 0) {
      this.popoverTarget.innerHTML = `
        <div class="p-3 text-xs text-gray-500">No hay mecánicos disponibles</div>
      `
      this.popoverTarget.classList.remove('hidden')
      return
    }

    const items = this.mechanics.map(m => {
      const isSelected = m.id === this.currentMechanicIdValue
      const selectedClass = isSelected ? 'bg-gray-100 font-semibold' : ''
      return `
        <button type="button"
                class="w-full text-left px-3 py-2 text-xs hover:bg-gray-50 flex items-center gap-2 ${selectedClass}"
                data-action="click->mechanic-selector#selectMechanic"
                data-mechanic-id="${m.id}"
                data-mechanic-name="${m.nombre_corto}">
          <span class="inline-block w-3 h-3 rounded-full flex-shrink-0" style="background-color: ${m.color};"></span>
          <span class="flex-1">${m.nombre_corto}</span>
          <span class="text-gray-400">${m.stops_count} paradas</span>
          ${isSelected ? '<svg class="w-3 h-3 text-green-600" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/></svg>' : ''}
        </button>
      `
    }).join('')

    this.popoverTarget.innerHTML = `
      <div class="py-1 text-xs font-semibold text-gray-500 px-3 border-b border-gray-100">Asignar a mecánico</div>
      ${items}
    `
    this.popoverTarget.classList.remove('hidden')
  }

  async selectMechanic(event) {
    event.stopPropagation()
    const mechanicId = parseInt(event.currentTarget.dataset.mechanicId)
    const mechanicName = event.currentTarget.dataset.mechanicName

    this.close()

    // Update the en_ruta_toggle controller's mechanicId if present
    const enRutaController = this.element.closest('[data-controller*="en-ruta-toggle"]')
    if (enRutaController) {
      enRutaController.setAttribute('data-en-ruta-toggle-mechanic-id-value', mechanicId)
    }

    // Dispatch event so other controllers can react
    window.dispatchEvent(new CustomEvent('mechanic-selected', {
      detail: {
        ticketId: this.ticketIdValue,
        mechanicId: mechanicId,
        mechanicName: mechanicName
      }
    }))

    console.log(`🔧 Mechanic selected: ${mechanicName} (${mechanicId}) for ticket ${this.ticketIdValue}`)
  }
}
